import { ListItem, Text, UnorderedList } from "@chakra-ui/react";
import React from "react";
import { SortingRule } from "react-table";

import {
  Link,
  SortableTable,
  SortableTableColumn,
} from "../../../../components";
import { PageOptions } from "../../../../hooks/useOffsetPagination";
import { formatDate } from "../../../../utils/datetime";
import { buildLeverLink } from "../../../../utils/lever";
import { Position, PositionJbpListItemFragment } from "../../../graphql";
import { SettingSwitch } from "./SettingSwitch";

interface RecordingSettingsListProps {
  positions: Array<PositionJbpListItemFragment>;
  updatePositionsRecordingSettings: (
    positionIds: Array<PositionJbpListItemFragment>,
    autoJoinOnOff: boolean,
    optInOnOff: boolean
  ) => Promise<any>;
  pageOptions?: PageOptions;
  sortBy: SortingRule<string>;
  showOptIn: boolean;
  loading: boolean;
}

const RecordingSettingsList: React.FC<RecordingSettingsListProps> = ({
  positions,
  updatePositionsRecordingSettings,
  pageOptions,
  sortBy,
  showOptIn = false,
  loading,
}) => {
  const columns: SortableTableColumn<Position>[] = [
    {
      Header: "Req ID",
      accessor: "requisitionId",
      id: "requisitionId",
      Cell: ({
        row: { original: position },
      }: {
        row: { original: PositionJbpListItemFragment };
      }) => {
        if (!position.requisitionId) return null;
        return (
          <Text overflowWrap="break-word" maxW="100px">
            {position.requisitionId}
          </Text>
        );
      },
    },
    {
      Header: "Title",
      accessor: "title",
      id: "title",
      Cell: ({
        row: { original: position },
      }: {
        row: { original: PositionJbpListItemFragment };
      }) => {
        let positionLink = `/position/${position.id}`;
        if (position.leverPostingId) {
          positionLink = buildLeverLink(position.leverPostingId);
        }
        if (position.greenhouseLink) {
          positionLink = position.greenhouseLink;
        }
        return (
          <Link
            href={positionLink}
            display="flex"
            alignItems="center"
            maxWidth="275px"
          >
            {position.title}
          </Link>
        );
      },
    },
    {
      Header: "Locations",
      accessor: "locations",
      id: "locations",
      disableSortBy: true,
      Cell: ({
        row: { original: position },
      }: {
        row: { original: PositionJbpListItemFragment };
      }) => {
        if (!position.locations) return null;
        if (position.locations.length === 0) return null;
        if (position.locations.length === 1)
          return (
            <Text overflowWrap="break-word" maxW="200px">
              {position.locations[0]}
            </Text>
          );
        return (
          <UnorderedList maxW="200px">
            {position.locations.map((l) => (
              <ListItem key={l}>{l}</ListItem>
            ))}
          </UnorderedList>
        );
      },
    },
    {
      Header: "Position Group",
      accessor: (position) => position.client.name,
      id: "client.name",
      Cell: ({
        row: { original: position },
      }: {
        row: { original: PositionJbpListItemFragment };
      }) => {
        if (!position.client.name) return null;
        return (
          <Text overflowWrap="break-word" maxW="200px">
            {position.client.name}
          </Text>
        );
      },
    },
    {
      Header: "Status",
      accessor: "atsStatus",
      disableSortBy: true,
      Cell: ({
        row: { original: position },
      }: {
        row: { original: PositionJbpListItemFragment };
      }) => {
        if (!position.atsStatus) return null;
        return (
          <Text overflowWrap="break-word" maxW="100px">
            {position.atsStatus}
          </Text>
        );
      },
    },
    {
      Header: "Added",
      accessor: "createdAt",
      Cell: ({
        row: { original: position },
      }: {
        row: { original: PositionJbpListItemFragment };
      }) => (
        // cspell: disable-next-line
        <Text style={{ fontVariantNumeric: "tabular-nums" }} maxW="102px">
          {formatDate(position.createdAt, {
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
          })}
        </Text>
      ),
    },
    {
      Header: "Record",
      accessor: "autoJoinInterview",
      Cell: ({
        row: { original: position },
      }: {
        row: { original: PositionJbpListItemFragment };
      }) => {
        return (
          <SettingSwitch
            onToggle={(newValue) =>
              updatePositionsRecordingSettings(
                [position],
                newValue,
                position.atsOptInInterview
              )
            }
            isChecked={position.autoJoinInterview}
          >
            {position.autoJoinInterview
              ? "Auto-join enabled"
              : "Auto-join disabled"}
          </SettingSwitch>
        );
      },
    },
  ];

  if (showOptIn) {
    const optInColumn: SortableTableColumn<Position> = {
      Header: "Consent",
      accessor: "atsOptInInterview",
      Cell: ({
        row: { original: position },
      }: {
        row: { original: PositionJbpListItemFragment };
      }) => {
        return (
          <SettingSwitch
            onToggle={(newValue) =>
              updatePositionsRecordingSettings(
                [position],
                position.autoJoinInterview,
                newValue
              )
            }
            isChecked={position.atsOptInInterview}
          >
            {position.atsOptInInterview ? "Opt-in consent" : "Opt-out consent"}
          </SettingSwitch>
        );
      },
    };
    columns.splice(columns.length - 1, 0, optInColumn);
  }

  return (
    <SortableTable<Position>
      columns={columns}
      columnWidths={[
        "102px",
        "200px",
        "200px",
        "200px",
        "100px",
        "102px",
        "102px",
        "102px",
      ]}
      data={positions as Position[]}
      autoResetSortBy={false}
      initialSort={sortBy}
      pageOptions={pageOptions}
      manualPagination
      width="1200px"
      loading={loading}
    />
  );
};

export default RecordingSettingsList;
